/**
 * Reserved folder names INSIDE a PDApp folder (`MyApp.pdapp/…`).
 *
 * Distinct from `reserved-folders.ts`, which names the DRIVE hierarchy
 * (`Library`, `Projects`, the legacy `_system` root). These are app-relative and
 * are only meaningful under a miniapp/PDApp folder.
 *
 * ── `_app*` is a reserved platform namespace (freeze doc 3c-i) ──────────
 *
 * Inside an app folder, `_app*` belongs to the platform:
 *
 *   MyApp.pdapp/
 *     manifest.json        identity
 *     index.html           entry
 *     _scripts/            backend code            (author's; the default since #1423 — `_api/` still runs)
 *     _queries/            named SQL ops           (author's; since #1423 — `_db/` still runs, its bare names deprecated)
 *     _schemas/            a schema per database   (author's; #1424)
 *     _appdata/            runtime data            (platform; excluded from the sealed release)
 *     _appsystem/          governance              (platform; signed, :ro to the running app)
 *       settings.json      this copy's values      (people only; kept on update; never ships — #1544)
 *       defaultSettings.json  shipped defaults     (the publisher; read by publish / install only — #1544)
 *     _defaultAppdata/     seed                    (platform; included in the release)
 *
 * The `appdata`/`defaultAppdata` partition grammar (freeze doc F-appdata) is a
 * NARROWER rule *inside* that namespace, not a competing one — which is why two
 * members of one family can carry opposite release semantics.
 *
 * Reservation is by CONVENTION today. Enforcing `_app*` against user-created
 * folders is a separate call with its own risk (existing apps may already hold an
 * `_app…` folder) and is deliberately not done here.
 */

/**
 * The app's control folder: `triggers.json` today; `connectors.json`,
 * `databases.json` and signed release metadata per the freeze doc's reserved
 * shapes. Platform-written, and READ-ONLY to the running app (an explicit
 * `target_type='app'` readcopy grant binds the subtree `:ro` at mount time), so a
 * script can never rewrite its own automation.
 *
 * Named for its ROLE, never for the format. Its predecessor `_miniapp` needs
 * renaming *only* because "miniapp" stopped being the product word; `_pdapp`
 * would have inherited that same fate, and `_system` alone was rejected because
 * an app author might plausibly create one — and `ensureAppControlFolder` is
 * get-or-create, so it would ADOPT their folder and stamp a read-only app grant
 * on it. Full reasoning: freeze doc 3c-i.
 */
export const APP_CONTROL_DIR = '_appsystem'

/**
 * #1544 (owner 2026-10-31) — THIS copy's values for what the control folder declares, in ONE file inside it:
 *
 *   _appsystem/env.json         declarations: variable names; secret names and descriptions
 *   _appsystem/triggers.json    declarations: each trigger / schedule (`id`, `name`, `when` / timing, `do`)
 *   _appsystem/settings.json    this copy's values: { "env": { vars, secrets: { NAME: { keyId, field } } },
 *                               "triggers": { paused, schedulesPaused, triggers: { id: { enabled } }, schedules: { id: { enabled } } } }
 *
 * Written by PEOPLE only (Settings, and their agent) and read-only to the running app — the control folder's explicit
 * app readcopy grant covers it, so no grant of its own. The ONE file of `_appsystem/` that is per copy: an update keeps
 * it, a release never ships it, and changing it is a setting, never an edit (release plan §6.2). Every whole-folder
 * consumer (publish, update, signing, fork detection — none built yet) excludes it through `APP_COPY_SETTINGS_FILES`.
 */
export const APP_SETTINGS_FILE = 'settings.json'

/** #1544 — the defaults a PUBLISHED app ships, same shape as `settings.json`. A name only today: written and read by
 *  publish / install (not built), never by the app's runtime. */
export const APP_DEFAULT_SETTINGS_FILE = 'defaultSettings.json'

/** #1544 — the files of the control folder that are THIS COPY's (kept on update, never shipped, never an edit). The one
 *  list every whole-folder consumer must use — never a copy of the name. */
export const APP_COPY_SETTINGS_FILES: readonly string[] = [APP_SETTINGS_FILE]

/**
 * #1423 (owner 2026-10-27) — the folder new apps keep their named SQL ops in. A `.sql` PATH reference runs the file at
 * that path, in ANY folder (`db.run('_queries/orders.sql')`, `db.run('_db/orders.sql')`, `db.run('reports/monthly.sql')`;
 * another app's through a pointer, `crm.chain/reports/monthly.sql`) — running it is reading it. New apps are made with
 * `_queries/` and call their ops by path (templates, the agent, the docs). The `_` prefix: never served, hook globs see it.
 */
export const APP_QUERIES_DIR = '_queries'

/** #1423 — the older ops folder. A BARE op name (`db.run('orders')`) always means `_db/orders.sql` — the shorthand is
 *  DEPRECATED (owner 2026-10-27): it keeps older apps working, new code names the path. */
export const LEGACY_QUERIES_DIR = '_db'

/** #1423 — the folder names a `.sql` route may stop at (`x.shared/site/_db/q.sql` names the app `site`). */
export const APP_QUERIES_DIRS = [APP_QUERIES_DIR, LEGACY_QUERIES_DIR] as const

/** #1424 — the conventional folder for an app's schema files (named per database in `databases.json`). A schema file is
 *  never run as an op: a `.sql` reference into this folder is refused. */
export const APP_SCHEMAS_DIR = '_schemas'

/**
 * #1423 — the DEFAULT folder for an app's backend scripts (`pdapp.scripts.run('_scripts/save.py')`, `triggers.json`
 * `run`, #1422's `api.json` routes). A CONVENTION, not a resolver: a script runs from whatever path names it (`_api/`,
 * the older default, keeps working); templates, the agent and the docs write new ones here. `_` prefix: never served.
 */
export const APP_SCRIPTS_DIR = '_scripts'

/**
 * The historical control-folder name. Still READ during the migration window
 * because the alternative corrupts data rather than merely degrading: with only
 * `_appsystem` known, get-or-create mints an empty `_appsystem/` **beside** a live
 * `_miniapp/`, orphaning that app's triggers AND tripping the backfill's own
 * collision rule so the app can never be migrated.
 *
 * REMOVED once the backfill is verified at zero — see freeze doc workstream F.
 * `shared/types/src/__tests__/no-raw-app-control-dir.test.ts` bans the raw
 * literals so that removal is a one-line deletion rather than a re-hunt.
 */
export const LEGACY_APP_CONTROL_DIR = '_miniapp'

/**
 * Accepted control-folder names, **canonical first**. Readers accept any;
 * writers emit `APP_CONTROL_DIR`. Order is load-bearing for resolution: when both
 * exist (a half-finished migration, or a hand-made folder), the canonical one
 * wins so behaviour matches what a fresh install would do.
 */
export const APP_CONTROL_DIRS = [APP_CONTROL_DIR, LEGACY_APP_CONTROL_DIR] as const

/**
 * Is `name` one of the accepted control-folder names?
 *
 * Case-SENSITIVE, unlike the extension predicates in `file-extensions.ts`. Those
 * answer "what kind of file is this?", where every industry consumer is
 * case-insensitive. This answers "is this the platform's own folder?" — a
 * platform-written path with one canonical spelling, so accepting `_AppSystem`
 * would mean adopting an author's differently-cased folder as our control plane.
 */
export function isAppControlDirName(name: string): boolean {
  return (APP_CONTROL_DIRS as readonly string[]).includes(name)
}

/**
 * Does an app-relative POSIX path lie AT or UNDER a control folder?
 *
 * First segment only — `content/_appsystem/x` is ordinary author content, not the
 * control plane. Mirrors the top-level-only discipline the freeze doc's stem
 * predicate uses, and for the same reason: an any-depth match reclassifies a
 * nested folder that merely shares the name.
 */
export function isAppControlPath(appRelativePath: string): boolean {
  const first = appRelativePath.split('/')[0]
  return !!first && isAppControlDirName(first)
}

/**
 * `isAppControlDirName` as a raw-SQL boolean, DERIVED from
 * {@link APP_CONTROL_DIRS} — the same shape (and the same reason) as
 * `miniappNameSqlPredicate` in `file-extensions.ts`.
 *
 * Some consumers filter inside SQL and cannot call the predicate: the debug-log
 * sweeper and the config-grant backfill both JOIN on the control folder by name.
 * A hand-written `p.name = '_miniapp'` there stops seeing migrated apps the
 * instant the backfill runs — and, being a maintenance sweep, does so with no
 * user-visible symptom at all.
 *
 * `col` is interpolated VERBATIM and must never carry user input; pass a trusted
 * column identifier. Callers wrap the result in `sql.raw()`.
 */
export function appControlDirSqlPredicate(col: string = 'name'): string {
  return `${col} IN (${APP_CONTROL_DIRS.map((d) => `'${d}'`).join(', ')})`
}

/** A PDApp's icon pictures — canonical dot-named files at the app folder's root (`features/files/core/icon-assets.ts`). */
export const MINIAPP_ICON_BG_FILENAME = '.miniapp-icon-bg.jpg'
export const MINIAPP_ICON_FG_FILENAME = '.miniapp-icon-fg.png'

/**
 * I16 — the files whose content other state follows ONLY through file events: `manifest.json` and the icon pictures
 * (the icon cache, `features/files/core/miniapp-icon-reconcile.ts`) and `triggers.json` in the control folder (the
 * schedules, the API's app-hooks dispatcher). A writer that batches its events (a large extract, Save to Drive) still
 * publishes one event per file with one of these names. By NAME, case-insensitive — a match outside an app is harmless,
 * each consumer checks where the file is.
 */
const APP_STATE_FILE_NAMES = new Set(['manifest.json', MINIAPP_ICON_BG_FILENAME, MINIAPP_ICON_FG_FILENAME, 'triggers.json'])
export function isAppStateFileName(name: string): boolean {
  return APP_STATE_FILE_NAMES.has(name.toLowerCase())
}
/** The same names, for a SQL `lower(name) = ANY(...)` filter. */
export const APP_STATE_FILE_NAME_LIST: readonly string[] = [...APP_STATE_FILE_NAMES]
/**
 * #1544 — `settings.json` carries the triggers' and schedules' on/off switches and timing, so the schedules follow it too —
 * but it is a very common file name (`.vscode/settings.json`, a package's settings): it counts ONLY directly inside an
 * app's control folder (/code-review: by name alone, an extracted repo published one event per settings.json).
 * `parentName`: the parent FOLDER's name (null when the parent is not a folder). The SQL twin is in
 * features/files/core/app-state-file-events.ts — change both together.
 */
export function isAppStateFile(name: string, parentName: string | null | undefined): boolean {
  if (isAppStateFileName(name)) return true
  return name === APP_SETTINGS_FILE && !!parentName && (APP_CONTROL_DIRS as readonly string[]).includes(parentName)
}
